import { useId } from 'react';
import { Tab, Tabs } from '@mui/material';
import { ReportTab } from '../../../Api/Report/reportApi';
import { TextSpan } from '../../Atoms/Text';
import { ReportDataTable, ReportToolbarVariant } from './ReportDataTable';
import { ReportWorkbook } from './useReportWorkbook';

export interface ReportTabsViewerProps {
  tabs: ReportTab[];
  content: Record<string, unknown>[];
  workbook: ReportWorkbook;
  toolbarVariant: ReportToolbarVariant;
  reportName: string;
}

const tabKey = (tab: ReportTab, index: number) => `${tab.position}-${index}-${tab.tab_name}`;

/** Shows a report's tabs, each over its own rows when it carries them. Hidden tabs stay mounted, because a tab's row count and its sheet both come from its own grid's filters. */
export const ReportTabsViewer = ({ tabs, content, workbook, toolbarVariant, reportName }: ReportTabsViewerProps) => {
  const showTabs = tabs.length > 1;
  const baseId = useId();
  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = (index: number) => `${baseId}-panel-${index}`;

  return (
    <>
      {showTabs && (
        <Tabs
          aria-label="Report tabs"
          value={workbook.activeIndex}
          onChange={(_, value: number) => workbook.setActiveIndex(value)}
          variant="scrollable"
          scrollButtons="auto"
          data-cy="report-tabs"
          sx={{ minHeight: 0, marginBottom: '0.5rem', '.MuiTab-root': { textTransform: 'none', minHeight: 0 } }}>
          {tabs.map((tab, index) => (
            <Tab
              key={tabKey(tab, index)}
              id={tabId(index)}
              aria-controls={panelId(index)}
              value={index}
              label={
                <span>
                  {tab.tab_name}
                  {workbook.counts[index] !== null && workbook.counts[index] !== undefined && (
                    <TextSpan
                      className="ml-2"
                      color="grey6">
                      {workbook.counts[index].toLocaleString()}
                    </TextSpan>
                  )}
                </span>
              }
            />
          ))}
        </Tabs>
      )}
      <div className="relative">
        {tabs.map((tab, index) => {
          const active = index === workbook.activeIndex;
          return (
            <div
              key={tabKey(tab, index)}
              id={panelId(index)}
              role="tabpanel"
              aria-labelledby={showTabs ? tabId(index) : undefined}
              aria-hidden={!active}
              className={active ? undefined : 'absolute invisible pointer-events-none w-full top-0'}>
              <ReportDataTable
                viewType={tab.table_type}
                content={tab.content ?? content}
                viewConfig={tab.view_config}
                viewName={tabs.length > 1 ? `${reportName} - ${tab.tab_name}` : reportName}
                toolbarVariant={toolbarVariant}
                apiRef={workbook.apiRefs[index]}
                onFilteredRowCountChange={(count) => workbook.onCount(index, count)}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};
